import React, { FC } from 'react';
import { SkeletonPlaceholder, GridItem, pxToRem } from '@modernhealth/vital-components';
import { layout } from '@modernhealth/vital-foundation';
import styled from '@emotion/styled';
import { mediaQueries } from 'src/shared/styles/variables';

import { ContentMaxWidthWrapper, dailyPauseBannerHeight } from '../pages/DailyPause';

type MilestonesSkeletonsProps = {
    elementsCount: number;
};

export const DailyPausePageSkeletons: FC<{ testId: string }> = ({ testId }) => {
    return (
        <Wrapper
            data-testid={testId}
            style={{ paddingTop: pxToRem(16), paddingBottom: pxToRem(24) }}
        >
            <SkeletonPlaceholder width="100%" height={dailyPauseBannerHeight} borderRadius={4} />

            <ContentMaxWidthWrapper style={{ margin: '0 auto' }}>
                <SkeletonPlaceholder width={260} height={40} marginTop={24} borderRadius={4} />

                <SkeletonPlaceholder width="100%" height={24} marginTop={8} borderRadius={4} />
                <SkeletonPlaceholder width="95%" height={24} marginTop={8} borderRadius={4} />
                <SkeletonPlaceholder width="80%" height={24} marginTop={8} borderRadius={4} />

                <SkeletonPlaceholder width="100%" height={24} marginTop={36} borderRadius={4} />
                <SkeletonPlaceholder width="95%" height={24} marginTop={8} borderRadius={4} />
                <SkeletonPlaceholder width="60%" height={24} marginTop={8} borderRadius={4} />

                <SkeletonPlaceholder width="100%" height={24} marginTop={36} borderRadius={4} />
                <SkeletonPlaceholder width="100%" height={24} marginTop={8} borderRadius={4} />
                <SkeletonPlaceholder width="99%" height={24} marginTop={8} borderRadius={4} />

                <SkeletonPlaceholder width="100%" height={24} marginTop={36} borderRadius={4} />
                <SkeletonPlaceholder width="85%" height={24} marginTop={8} borderRadius={4} />
                <SkeletonPlaceholder width="30%" height={24} marginTop={8} borderRadius={4} />
            </ContentMaxWidthWrapper>
        </Wrapper>
    );
};

export const DailyPauseStatsSkeletons: FC = () => {
    return (
        <Wrapper>
            <SkeletonPlaceholder width={260} height={40} marginTop={70} borderRadius={4} />
            <SkeletonPlaceholder width={300} height={24} marginTop={8} borderRadius={4} />

            <Grid>
                <GridItem lg={2} md={2} column>
                    <div style={{ height: 88 }}>
                        <SkeletonPlaceholder
                            width={210}
                            height={88}
                            marginRight={12}
                            borderRadius={4}
                        />
                        <SkeletonPlaceholder width={210} height={88} borderRadius={4} />
                    </div>

                    <SkeletonPlaceholder
                        width="100%"
                        height={460}
                        marginTop={12}
                        borderRadius={4}
                    />
                </GridItem>

                <GridItem lg={2} md={2} column>
                    <SkeletonPlaceholder width={420} height={36} marginTop={0} borderRadius={4} />

                    <MilestoneMinimizedListSkeletons />

                    <MinimizedCompletedDailyPauseSkeletons />
                </GridItem>
            </Grid>
        </Wrapper>
    );
};

export const MinimizedCompletedDailyPauseSkeletons: FC = () => (
    <div style={{ marginTop: 4 }}>
        <SkeletonPlaceholder width={420} height={36} borderRadius={4} />

        <div style={{ marginTop: 20, display: 'flex', gap: 20 }}>
            <DpCardSkeletons />
            <DpCardSkeletons />
        </div>
    </div>
);

const MilestonesSkeletons: FC<MilestonesSkeletonsProps> = ({ elementsCount }) => (
    <>
        {[...Array(elementsCount)].map((_, index) => (
            <MilestoneBadgeSkeletonWrapper key={index}>
                <MilestoneBadgeSkeleton>
                    <SkeletonPlaceholder width={70} height={70} marginTop={0} borderRadius={35} />
                </MilestoneBadgeSkeleton>
                <SkeletonPlaceholder width={50} height={16} marginTop={16} borderRadius={4} />
            </MilestoneBadgeSkeletonWrapper>
        ))}
    </>
);

const MilestoneMinimizedListSkeletons: FC = () => (
    <div style={{ marginTop: 24, marginBottom: 46, marginLeft: 4, display: 'flex', gap: 18 }}>
        <MilestonesSkeletons elementsCount={5} />
    </div>
);

export const MilestoneListSkeletons: FC<MilestonesSkeletonsProps> = ({ elementsCount }) => (
    <div style={{ display: 'flex', flexDirection: 'column', marginTop: pxToRem(20) }}>
        <SkeletonPlaceholder height={50} width={240} marginTop={22} marginBottom={44} />
        <SkeletonPlaceholder height={40} width={120} marginBottom={52} />
        <MilestoneListWrapper>
            <MilestonesSkeletons elementsCount={elementsCount} />
        </MilestoneListWrapper>
    </div>
);

const DpCardSkeletons: FC = () => {
    return (
        <div style={{ marginTop: 4, marginLeft: 4, display: 'flex', flexDirection: 'column' }}>
            <SkeletonPlaceholder width={204} height={160} borderRadius={4} />

            <SkeletonPlaceholder width={170} height={25} marginTop={10} borderRadius={4} />

            <SkeletonPlaceholder width={100} height={18} marginTop={10} borderRadius={4} />
        </div>
    );
};

const Wrapper = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    display: 'flex',
    flexDirection: 'column',
});

const Grid = styled.div({
    marginTop: 38,
    display: 'flex',

    flexDirection: 'row',
    gap: 260,

    [mediaQueries.md]: {
        flexDirection: 'column',
        gap: 20,
    },
});

const MilestoneListWrapper = styled.ul({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'start',
    gap: pxToRem(16),
    padding: 0,
    margin: 0,
});

const MilestoneBadgeSkeletonWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const MilestoneBadgeSkeleton = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});
